import React, { useEffect, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { UUID, RootState } from '@umanforge/common';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon } from '@common_components/icons/plus.icon';
import { MinusIcon } from '@common_components/icons/minus.icon';
import { EditBoxIcon } from '@common_components/icons/editBox.icon';
import { ChevronsLeftIcon } from '@common_components/icons/chevronsLeft.icon';
import { ChevronsRightIcon } from '@common_components/icons/chevronsRight.icon';
import InputText from '@common_components/inputs/textInput.component';
import SubmitButton from '@common_components/buttons/submitButton.component';
import AddChartSettingCard from './chartAddSettingCard.component';
import ChartSettingsListSection from './chartSettingsListSection.component';


import { getReadCategory } from './chartReadRange.utils';
import type {
  ChartSection,
  ChartSectionListHandle,
  ChartSectionListProps,
  NewChartSettingFormState,
  PendingDeletedChartSetting,
  PendingNewChartSetting,
  PendingUpdatedChartSetting,
} from './chartSectionList.interfaces';
import { ChartOfSectionService } from 'services/chartOfSection.service';
import {
  ChartOfSectionApi,
  ChartReadStart,
  ChartTargetType,
} from 'interfaces/chartOfSection.interface';

const isPersistedSection = ( sectionId: string ): boolean => {
  return !sectionId.startsWith( 'section-' );
};

/** Le back n'accepte pas le préfixe 'a' (annuel) — on le normalise en 'y' */
const normalizeReadStart = ( value: string ): ChartReadStart => {
  return value.replace( /^a/, 'y' ) as ChartReadStart;
};



export const ChartSectionList = forwardRef<ChartSectionListHandle, ChartSectionListProps>( (
  {
    sections,
    onChange,
    labelPosition = 'top',
    chartUnitOptions = [],
    managerOptions = [],
    themeOptions = [],
    defaultManagerId,
    defaultChartUnitId,
    onPendingSettingsChange,
  }, ref ) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );

  const getDefaultNewSetting = (): NewChartSettingFormState => {
    return {
      chartUnitId: defaultChartUnitId ?? '__none__',
      managerId: defaultManagerId ?? '',
      title: '',
      dataName: '',
      type: 'basic_line',
      color: '#0000ff',
      targetName: '',
      targetType: 'min',
      targetColor: '#ff0000',
      targetValue: '',
      createTicketOnTargetMiss: '0',
      ticketCreationHour: '',
      ticketTheme: '',
      sendAlertTime: '',
      alertThemeId: '',
      displayMin: '0',
      displayMax: '0',
      deltaMinMax: '',
      readStart: 'd-1',
      readEnd: 'd-15',
      additionalIndicator: 'none',
      hasTarget: '0',
      incrementValue: '',
      incrementFrequencyDays: '',
    };
  };

  const [ isCreating, setIsCreating ] = useState( false );
  const [ isEditingMode, setIsEditingMode ] = useState( false );
  const [ newSectionName, setNewSectionName ] = useState( '' );
  const [ editingSectionId, setEditingSectionId ] = useState<string | null>( null );
  const [ editingName, setEditingName ] = useState( '' );
  const [ tempSectionId, setTempSectionId ] = useState<string | null>( null );
  const [ selectedSectionId, setSelectedSectionId ] = useState<string | null>( null );
  const [ showSettingForm, setShowSettingForm ] = useState( false );
  const [ editingChartSettingId, setEditingChartSettingId ] = useState<UUID | null>( null );

  const [ chartSettings, setChartSettings ] = useState<ChartOfSectionApi[]>( [] );

  const [ pendingSettings, setPendingSettings ] = useState<PendingNewChartSetting[]>( [] );
  const [ pendingUpdatedSettings, setPendingUpdatedSettings ] = useState<PendingUpdatedChartSetting[]>( [] );
  const [ pendingDeletedSettings, setPendingDeletedSettings ] = useState<PendingDeletedChartSetting[]>( [] );

  const [ originalSetting, setOriginalSetting ] = useState<NewChartSettingFormState | null>( null );
  const [ formChanged, setFormChanged ] = useState<boolean>( false );
  const [ isLoadingSettings, setIsLoadingSettings ] = useState( false );
  const [ , setIsSubmittingSettings ] = useState( false );
  const [ loadSettingsError, setLoadSettingsError ] = useState<string | null>( null );
  const [ newSetting, setNewSetting ] = useState<NewChartSettingFormState>( getDefaultNewSetting );

  const initialChartOrderRef = useRef<UUID[]>( [] );
  const loadedSectionIdRef = useRef<string | null>( null );

  useEffect( () => {
    if ( !editingSectionId ) {
      return;
    }

    const currentSection = sections.find( section => { return section.id === editingSectionId; } );
    if ( !currentSection || !isPersistedSection( currentSection.id ) ) {
      setChartSettings( [] );
      setLoadSettingsError( null );
      return;
    }

    setIsLoadingSettings( true );
    setLoadSettingsError( null );

    ChartOfSectionService.getBySection( currentSection.id as UUID, token, dispatch )
      .then( data => {
        setChartSettings( data );
        initialChartOrderRef.current = data.map( s => { return s.id; } );
        loadedSectionIdRef.current = currentSection.id;

        if ( data.length > 0 ) {
          setNewSetting( prev => {
            return {
              ...prev,
              chartUnitId: data[ 0 ].chartUnit?.id ?? '__none__',
              managerId: data[ 0 ].manager.id as UUID,
            };
          } );
        } else {
          setNewSetting( prev => {
            return {
              ...prev,
              chartUnitId: defaultChartUnitId ?? '__none__',
              managerId: defaultManagerId ?? '',
            };
          } );
        }
      } )
      .catch( () => {
        setLoadSettingsError( t( 'translation:chart_settings_load_error' ) );
      } )
      .finally( () => {
        setIsLoadingSettings( false );
      } );
  }, [ defaultChartUnitId, defaultManagerId, dispatch, editingSectionId, sections, t, token ] );

  useEffect( () => {
    setNewSetting( prev => {
      if ( prev.managerId !== '' || ( prev.chartUnitId !== '' && prev.chartUnitId !== '__none__' ) ) {
        return prev;
      }

      return {
        ...prev,
        managerId: defaultManagerId ?? prev.managerId,
        chartUnitId: defaultChartUnitId ?? prev.chartUnitId,
      };
    } );
  }, [ defaultChartUnitId, defaultManagerId ] );

  const updatePendingSettings = ( updater: ( prev: PendingNewChartSetting[] ) => PendingNewChartSetting[] ) => {
    setPendingSettings( prev => {
      const next = updater( prev );
      return next;
    } );
  };

  const updatePendingUpdatedSettings = ( updater: ( prev: PendingUpdatedChartSetting[] ) => PendingUpdatedChartSetting[] ) => {
    setPendingUpdatedSettings( prev => {
      const next = updater( prev );
      return next;
    } );
  };

  const updatePendingDeletedSettings = ( updater: ( prev: PendingDeletedChartSetting[] ) => PendingDeletedChartSetting[] ) => {
    setPendingDeletedSettings( prev => {
      const next = updater( prev );
      return next;
    } );
  };

  const moveSection = ( fromIndex: number, toIndex: number ) => {
    if ( toIndex < 0 || toIndex >= sections.length ) {
      return;
    }

    const updated = [ ...sections ];
    const [ moved ] = updated.splice( fromIndex, 1 );
    updated.splice( toIndex, 0, moved );
    onChange( updated );
  };

  const createTempSectionId = (): string => {
    return `section-${dayjs().valueOf()}-${Math.floor( Math.random() * 10000 )}`;
  };


  const handleAddSection = () => {
    const draftSectionId = createTempSectionId();
    setIsCreating( true );
    setIsEditingMode( false );
    setEditingSectionId( null );
    setTempSectionId( draftSectionId );
    setEditingName( '' );
    setNewSectionName( '' );
    setSelectedSectionId( null );
    setShowSettingForm( false );
    setEditingChartSettingId( null );
  };

  const handleConfirmCreate = () => {
    const trimmed = newSectionName.trim();
    if ( trimmed === '' ) {
      return;
    }

    const tempId = tempSectionId ?? createTempSectionId();
    const position = sections.length;
    const newSection = {
      id: tempId,
      name: trimmed,
      position,
    };
    onChange( [ ...sections, newSection ] );

    setIsCreating( false );
    setIsEditingMode( false );
    // Keep the UI in list mode after create; do not switch to rename mode.
    setEditingSectionId( null );
    setSelectedSectionId( tempId );
    setTempSectionId( null );
    setNewSectionName( '' );
    setEditingName( '' );
    setShowSettingForm( false );
    setEditingChartSettingId( null );
    setNewSetting( getDefaultNewSetting() );
    onPendingSettingsChange?.( true );
  };

  const handleStartEdit = ( section: ChartSection ) => {
    setIsCreating( false );
    setIsEditingMode( true );
    setNewSectionName( '' );
    setEditingSectionId( section.id );
    setEditingName( section.name );
    setSelectedSectionId( section.id );
    setShowSettingForm( false );
    setEditingChartSettingId( null );
  };

  const handleConfirmEdit = () => {
    if ( !editingSectionId ) {
      return;
    }
    const trimmed = editingName.trim();
    if ( trimmed === '' ) {
      return;
    }

    const updated = sections.map( section => {
      if ( section.id === editingSectionId ) {
        return { ...section, name: trimmed };
      }
      return section;
    } );

    onChange( updated );
    setIsEditingMode( false );
    setEditingSectionId( null );
    setEditingName( '' );
    onPendingSettingsChange?.( true );
  };

  const handleCancelEdit = () => {
    setIsCreating( false );
    setIsEditingMode( false );
    setChartSettings( [] );
    setPendingSettings( pendingSettings.filter( setting => { return setting.sectionId != editingSectionId; } )  );
    setPendingUpdatedSettings( pendingUpdatedSettings.filter( setting => { return chartSettings.find( item => { return item.id === setting.id; } )?.chartSection.id !== editingSectionId; } ) );
    setPendingDeletedSettings( pendingDeletedSettings.filter( setting => { return setting.setting.chartSection.id !== editingSectionId; } ) );
    setEditingSectionId( null );
    setEditingName( '' );
    setNewSectionName( '' );
    setTempSectionId( null );
    setLoadSettingsError( null );
    setShowSettingForm( false );
    setEditingChartSettingId( null );
  };

  const handleRemoveSection = () => {
    if ( !selectedSectionId ) {
      return;
    }

    const updated = sections.filter( section => { return section.id !== selectedSectionId; } );

    if ( updated.length === sections.length ) {
      return;
    }

    onChange( updated );
    setPendingSettings( prev => {
      return prev.filter( setting => { return setting.sectionId !== selectedSectionId; } );
    } );
    setSelectedSectionId( null );

    if ( editingSectionId === selectedSectionId ) {
      setEditingSectionId( null );
      setEditingName( '' );
    }
  };

  const handleCancelCreate = () => {
    if ( tempSectionId ) {
      setPendingSettings( prev => {
        return prev.filter( setting => { return setting.sectionId !== tempSectionId; } );
      } );
    }
    setIsCreating( false );
    setIsEditingMode( false );
    setNewSectionName( '' );
    setEditingSectionId( null );
    setTempSectionId( null );
    setEditingName( '' );
    setShowSettingForm( false );
    setEditingChartSettingId( null );
  };

  const handleChangeNewSettingField = ( field: keyof NewChartSettingFormState, value: string ) => {
    setNewSetting( prev => {
      return {
        ...prev,
        [ field ]: value,
      };
    } );
    setFormChanged( true );
  };

  const handleSaveChartSetting = () => {
    if ( !newSetting.chartUnitId || !newSetting.title.trim() || !newSetting.dataName.trim() || !newSetting.managerId ) {
      return;
    }
    const startCategory = getReadCategory( newSetting.readStart );
    const endCategory = getReadCategory( newSetting.readEnd );
    if ( !startCategory || !endCategory || startCategory !== endCategory ) {
      return;
    }

    // If type is auto increment gauge, increment fields are required and must be >= 1
    if ( newSetting.type === 'auto_increment_gauge' ) {
      const incVal = newSetting.incrementValue !== '' ? Number( newSetting.incrementValue ) : NaN;
      const incFreq = newSetting.incrementFrequencyDays !== '' ? Number( newSetting.incrementFrequencyDays ) : NaN;
      if ( !Number.isFinite( incVal ) || incVal < 1 || !Number.isFinite( incFreq ) || incFreq < 1 ) {
        setLoadSettingsError( t( 'translation:increment_value' ) + ' & ' + t( 'translation:increment_frequency_days' ) + ' ' + t( 'translation:enter_min_value' ) );
        return;
      }
    }
    setLoadSettingsError( null );

    if ( editingChartSettingId ) {
      updatePendingUpdatedSettings( prev => {
        const filtered = prev.filter( setting => {return setting.id !== editingChartSettingId;} );
        return [ ...filtered, { id: editingChartSettingId, values: { ...newSetting } } ];
      } );
      setChartSettings( prev => {
        return prev.map( setting => {
          if ( setting.id !== editingChartSettingId ) {
            return setting;
          }
          return {
            ...setting,
            chartUnit: newSetting.chartUnitId === '__none__'
              ? null
              : {
                ...setting.chartUnit,
                id: newSetting.chartUnitId as UUID,
                value: setting.chartUnit?.value ?? '',
              },
            manager: {
              ...setting.manager,
              id: newSetting.managerId as UUID,
            },
            title: newSetting.title,
            dataName: newSetting.dataName,
            type: newSetting.type,
            color: newSetting.color,
            targetName: newSetting.targetName,
            targetType: newSetting.targetType,
            targetColor: newSetting.targetColor,
            createTicketOnTargetMiss: newSetting.createTicketOnTargetMiss === '1',
            sendAlertTime: newSetting.createTicketOnTargetMiss === '1' ? ( newSetting.ticketCreationHour || null ) : null,
            alertThemeId: newSetting.createTicketOnTargetMiss === '1' ? newSetting.alertThemeId as UUID : null,
            readStart: newSetting.readStart,
            readEnd: newSetting.readEnd,
            additionalIndicator: newSetting.additionalIndicator,
          };
        } );
      } );
    } else {
      if ( isCreating ) {
        const draftSectionId = tempSectionId ?? createTempSectionId();
        if ( !tempSectionId ) {
          setTempSectionId( draftSectionId );
        }
        updatePendingSettings( prev => {
          return [ ...prev, { sectionId: draftSectionId, values: { ...newSetting } } ];
        } );
      } else if ( editingSectionId ) {
        updatePendingSettings( prev => {
          return [ ...prev, { sectionId: editingSectionId, values: { ...newSetting } } ];
        } );
      }
    }

    setNewSetting( prev => {return {
      ...prev,
      title: '',
      dataName: '',
      targetName: '',
      targetValue: '',
      createTicketOnTargetMiss: '0',
      ticketCreationHour: '',
      ticketTheme: '',
      alertThemeId: '',
      displayMin: '0',
      displayMax: '0',
      deltaMinMax: '',
      incrementValue: '',
      incrementFrequencyDays: '',
    };} );
    setFormChanged( false );
    setShowSettingForm( false );
    setEditingChartSettingId( null );
  };

  const handleRemovePending = ( index: number ) => {
    updatePendingSettings( prev => { return prev.filter( ( _, i ) => { return i !== index; } ); } );
  };

  const handleMovePendingSetting = ( fromIndex: number, toIndex: number ) => {
    updatePendingSettings( prev => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length
      ) {
        return prev;
      }
      const updated = [ ...prev ];
      const [ moved ] = updated.splice( fromIndex, 1 );
      updated.splice( toIndex, 0, moved );
      return updated;
    } );
  };

  const handleRemovePendingUpdate = ( chartInfoId: UUID ) => {
    updatePendingUpdatedSettings( prev => {
      return prev.filter( setting => { return setting.id !== chartInfoId; } );
    } );
  };

  const handleMarkDeleteChartSetting = ( setting: ChartOfSectionApi ) => {
    setChartSettings( prev => {
      return prev.filter( current => { return current.id !== setting.id; } );
    } );
    updatePendingUpdatedSettings( prev => {
      return prev.filter( current => { return current.id !== setting.id; } );
    } );
    updatePendingDeletedSettings( prev => {
      const hasAlreadyPendingDelete = prev.some( current => { return current.setting.id === setting.id; } );
      if ( hasAlreadyPendingDelete ) {
        return prev;
      }
      return [ ...prev, { setting } ];
    } );
    if ( editingChartSettingId === setting.id ) {
      setEditingChartSettingId( null );
      setShowSettingForm( false );
    }
    setLoadSettingsError( null );
  };

  // Move a chart setting up or down in the list
  const handleMoveChartSetting = ( fromIndex: number, toIndex: number ) => {
    setChartSettings( prev => {
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= prev.length ||
        toIndex >= prev.length
      ) {
        return prev;
      }
      const updated = [ ...prev ];
      const [ moved ] = updated.splice( fromIndex, 1 );
      updated.splice( toIndex, 0, moved );
      return updated;
    } );
    onPendingSettingsChange?.( true );
  };

  const handleSubmitPending = async ( sectionIdMap?: Map<string, UUID> ) => {
    const currentEditingSectionId = loadedSectionIdRef.current;
    const currentOrder = chartSettings.map( s => { return s.id; } );
    const pendingSectionPositionCursor = new Map<string, number>();
    if ( currentEditingSectionId && isPersistedSection( currentEditingSectionId ) ) {
      pendingSectionPositionCursor.set( currentEditingSectionId, chartSettings.length );
    }
    const hasOrderChanged = ( currentOrder.length === initialChartOrderRef.current.length && currentOrder.length > 0 )
      && currentOrder.some( ( id, idx ) => { return id !== initialChartOrderRef.current[ idx ]; } );

    if (
      pendingSettings.length === 0 &&
      pendingUpdatedSettings.length === 0 &&
      pendingDeletedSettings.length === 0 &&
      !hasOrderChanged
    ) {
      return;
    }
    setIsSubmittingSettings( true );
    setLoadSettingsError( null );
    try {
      const failedPendingSettings: PendingNewChartSetting[] = [];

      for ( const pending of pendingSettings ) {
        const resolvedSectionId = sectionIdMap?.get( pending.sectionId ) ?? pending.sectionId;
        const nextPositionInSection = pendingSectionPositionCursor.get( resolvedSectionId ) ?? 0;
        pendingSectionPositionCursor.set( resolvedSectionId, nextPositionInSection + 1 );
        if ( !isPersistedSection( resolvedSectionId ) ) {
          failedPendingSettings.push( pending );
        } else {
          const createPayload = {
            chartSectionId: resolvedSectionId as UUID,
            chartUnitId: ( pending.values.chartUnitId === '__none__' ? null : pending.values.chartUnitId ) as UUID,
            managerId: pending.values.managerId as UUID,
            title: pending.values.title,
            dataName: pending.values.dataName,
            type: pending.values.type,
            color: pending.values.color,
            targetName: pending.values.hasTarget === '1' ? pending.values.targetName : null,
            targetType: pending.values.hasTarget === '1' ? pending.values.targetType as ChartTargetType : null,
            targetColor: pending.values.hasTarget === '1' ? pending.values.targetColor : null,
            targetValue: pending.values.hasTarget === '1' ? ( Number( pending.values.targetValue ) || 0 ) : null,
            sendAlertTime: pending.values.createTicketOnTargetMiss === '1' ? ( pending.values.ticketCreationHour || null ) : null,
            alertThemeId: pending.values.createTicketOnTargetMiss === '1' ? pending.values.alertThemeId as UUID : null,
            readStart: normalizeReadStart( pending.values.readStart ),
            readEnd: pending.values.readEnd,
            additionalIndicator: pending.values.additionalIndicator,
            isUsedMin: pending.values.displayMin === '1',
            isUsedMax: pending.values.displayMax === '1',
            deltaMinMax: ( pending.values.displayMin === '1' || pending.values.displayMax === '1' ) && pending.values.deltaMinMax !== ''
              ? Number( pending.values.deltaMinMax )
              : null,
            position: nextPositionInSection,
            incrementValue: pending.values.incrementValue !== '' ? Number( pending.values.incrementValue ) : null,
            incrementFrequencyDays: pending.values.incrementFrequencyDays !== '' ? Number( pending.values.incrementFrequencyDays ) : null,
          };
          const createdId = await ChartOfSectionService.createChartOnSection( createPayload, token, dispatch );

          if ( !createdId ) {
            failedPendingSettings.push( pending );
          }
        }
      }

      const failedPendingUpdates: PendingUpdatedChartSetting[] = [];

      for ( const pending of pendingUpdatedSettings ) {
        const hasTargetFields = pending.values.hasTarget === '1';

        const updatePayload = {
          chartUnitId: pending.values.chartUnitId === '__none__' ? null : ( pending.values.chartUnitId || undefined ),
          title: pending.values.title,
          dataName: pending.values.dataName,
          type: pending.values.type,
          color: pending.values.color,
          targetName: hasTargetFields ? pending.values.targetName : null,
          targetType: hasTargetFields ? pending.values.targetType as ChartTargetType : null,
          targetColor: hasTargetFields ? pending.values.targetColor : null,
          targetValue: hasTargetFields && pending.values.targetValue.trim() !== '' ? ( Number( pending.values.targetValue ) || 0 ) : ( hasTargetFields ? 0 : null ),
          sendAlertTime: pending.values.createTicketOnTargetMiss === '1' ? ( pending.values.ticketCreationHour || null ) : null,
          alertThemeId: pending.values.createTicketOnTargetMiss === '1' ? pending.values.alertThemeId as UUID : null,
          readStart: normalizeReadStart( pending.values.readStart ),
          readEnd: pending.values.readEnd,
          additionalIndicator: pending.values.additionalIndicator,
          isUsedMin: pending.values.displayMin === '1',
          isUsedMax: pending.values.displayMax === '1',
          deltaMinMax: ( pending.values.displayMin === '1' || pending.values.displayMax === '1' ) && pending.values.deltaMinMax !== ''
            ? Number( pending.values.deltaMinMax )
            : null,
          incrementValue: pending.values.incrementValue !== '' ? Number( pending.values.incrementValue ) : null,
          incrementFrequencyDays: pending.values.incrementFrequencyDays !== '' ? Number( pending.values.incrementFrequencyDays ) : null,
        };
        const isUpdated = await ChartOfSectionService.updateChartOnSection( pending.id, updatePayload, token, dispatch );

        if ( !isUpdated ) {
          failedPendingUpdates.push( pending );
        }
      }

      const failedPendingDeletes: PendingDeletedChartSetting[] = [];

      for ( const pending of pendingDeletedSettings ) {
        const isDeleted = await ChartOfSectionService.deleteChartOnSection( pending.setting.id, token, dispatch );

        if ( !isDeleted ) {
          failedPendingDeletes.push( pending );
        }
      }

      updatePendingSettings( () => {return failedPendingSettings;} );
      updatePendingUpdatedSettings( () => {return failedPendingUpdates;} );
      updatePendingDeletedSettings( () => {return failedPendingDeletes;} );

      if ( currentEditingSectionId && isPersistedSection( currentEditingSectionId ) ) {
        const hasOrderChangedNow = ( currentOrder.length === initialChartOrderRef.current.length && currentOrder.length > 0 )
          && currentOrder.some( ( id, idx ) => { return id !== initialChartOrderRef.current[ idx ]; } );

        if ( hasOrderChangedNow ) {
          const positions = currentOrder.map( ( id, idx ) => { return { id: id as UUID, position: idx }; } );
          await ChartOfSectionService.updateChartsPositions( currentEditingSectionId as UUID, positions, token, dispatch );
          initialChartOrderRef.current = currentOrder as UUID[];
        }
      }
    } catch {
      setLoadSettingsError( t( 'translation:chart_settings_save_error' ) );
    } finally {
      setIsSubmittingSettings( false );
    }
  };

  const handleRestorePendingDelete = ( chartInfoId: UUID ) => {
    const toRestore = pendingDeletedSettings.find( p => { return p.setting.id === chartInfoId; } );
    updatePendingDeletedSettings( prev => {
      return prev.filter( current => { return current.setting.id !== chartInfoId; } );
    } );
    if ( toRestore ) {
      setChartSettings( prev => { return [ ...prev, toRestore.setting ]; } );
    }
  };

  const hasTargetValue = newSetting.hasTarget === '1';
  const activeSectionId = editingSectionId ?? tempSectionId;
  const isSectionFormOpen = isCreating || isEditingMode;

  useImperativeHandle( ref, () => { return { submitPending: handleSubmitPending }; }, [ pendingSettings, pendingUpdatedSettings, editingSectionId, sections, token ] );
  return (
    <div className={`flex ${labelPosition === 'top' ? 'flex-col gap-2' : 'items-center gap-4'}`}>
      <div className={labelPosition === 'left' ? 'w-1/4' : ''}>
        <h3 className="font-bold mb-3">
          {t( 'translation:chart_sections' )}
        </h3>
        <p className="text-xs text-gray-500">
          {t( 'translation:chart_sections_description' )}
        </p>
      </div>

      <div className={`flex flex-col gap-3 ${labelPosition === 'left' ? 'w-3/4' : 'w-full'}`}>
        {isSectionFormOpen ? (
          <div className="p-4 border border-blue-200 rounded-md bg-blue-50 w-full flex flex-col gap-4 mx-0">
            <p className="text-xs font-medium text-gray-700 mb-1">
              {isEditingMode
                ? t( 'translation:rename_chart_section' )
                : t( 'translation:create_chart_section' )}
            </p>
            <InputText
              className="flex-1"
              label={t( 'translation:section_name_placeholder' ) as string}
              labelPosition="top"
              isRequired
              value={isEditingMode ? editingName : newSectionName}
              onChange={val => {
                if ( isEditingMode ) {setEditingName( val );}
                else {setNewSectionName( val );}
              }}
              maxCharacter={45}
            />
            <div>
              <p className="text-xs font-medium text-gray-700 mb-2">
                {t( 'translation:chart_settings_for_section' )}
              </p>
              {showSettingForm ? (
                <AddChartSettingCard
                  t={t}
                  newSetting={newSetting}
                  hasTargetValue={hasTargetValue}
                  isEditingExisting={editingChartSettingId !== null}
                  isEditUnchanged={editingChartSettingId !== null ? !formChanged : false}
                  originalSetting={originalSetting}
                  chartUnitOptions={chartUnitOptions}
                  managerOptions={managerOptions}
                  themeOptions={themeOptions}
                  onChangeField={handleChangeNewSettingField}
                  onCreate={handleSaveChartSetting}
                  onBack={() => {
                    setShowSettingForm( false );
                    setEditingChartSettingId( null );
                    setOriginalSetting( null );
                    setFormChanged( false );
                    setFormChanged( false );
                  }}
                />
              ) : (
                <ChartSettingsListSection
                  t={t}
                  isLoadingSettings={isLoadingSettings}
                  loadSettingsError={loadSettingsError}
                  chartSettings={ chartSettings }
                  pendingSettings={ pendingSettings.filter( setting => { return setting.sectionId === activeSectionId; } ) }
                  pendingUpdatedSettings={ pendingUpdatedSettings.filter( setting => { return chartSettings.find( item => { return item.id === setting.id; } )?.chartSection.id === activeSectionId; } ) }
                  pendingDeletedSettings={ pendingDeletedSettings.filter( setting => { return setting.setting.chartSection.id === activeSectionId; } ) }
                  onAdd={() => {
                    setNewSetting( {
                      ...getDefaultNewSetting(),
                      chartUnitId: chartSettings.length > 0 ? ( chartSettings[ 0 ].chartUnit?.id ?? '__none__' ) : ( defaultChartUnitId ?? '__none__' ),
                      managerId: chartSettings.length > 0 ? chartSettings[ 0 ].manager.id as UUID : defaultManagerId ?? '',
                    } );
                    setFormChanged( false );
                    setEditingChartSettingId( null );
                    setLoadSettingsError( null );
                    setShowSettingForm( true );
                  }}
                  onSelectSetting={( !editingSectionId || isCreating ) ? undefined : ( setting => {
                    setNewSetting( {
                      chartUnitId: setting.chartUnit?.id ?? '__none__',
                      managerId: setting.manager.id as UUID,
                      title: setting.title,
                      dataName: setting.dataName,
                      type: setting.type,
                      color: setting.color,
                      targetName: setting.targetName ?? '',
                      targetType: setting.targetType ?? '',
                      targetColor: setting.targetColor ?? '',
                      targetValue: setting.targetValue != null ? String( setting.targetValue ) : '',
                      createTicketOnTargetMiss: ( setting.sendAlertTime ? '1' : ( setting.alertThemeId ? '1' : '0' ) ),
                      ticketCreationHour: setting.sendAlertTime ? setting.sendAlertTime.slice( 0, 5 ) : '',
                      ticketTheme: setting.alertThemeId ?? '',
                      sendAlertTime: setting.sendAlertTime ? setting.sendAlertTime.slice( 0, 5 ) : '',
                      alertThemeId: setting.alertThemeId ?? '',
                      hasTarget: ( setting.targetValue != null || !!setting.targetName ) ? '1' : '0',
                      displayMin: setting.isUsedMin ? '1' : '0',
                      displayMax: setting.isUsedMax ? '1' : '0',
                      deltaMinMax: setting.deltaMinMax != null ? String( setting.deltaMinMax ) : '',
                      incrementValue: setting.incrementValue != null ? String( setting.incrementValue ) : '',
                      incrementFrequencyDays: setting.incrementFrequencyDays != null ? String( setting.incrementFrequencyDays ) : '',
                      readStart: normalizeReadStart( setting.readStart ),
                      readEnd: setting.readEnd,
                      additionalIndicator: setting.additionalIndicator,
                    } );
                    setEditingChartSettingId( setting.id );
                    setOriginalSetting( {
                      chartUnitId: setting.chartUnit?.id ?? '__none__',
                      managerId: setting.manager.id as UUID,
                      title: setting.title,
                      dataName: setting.dataName,
                      type: setting.type,
                      color: setting.color,
                      targetName: setting.targetName ?? '',
                      targetType: setting.targetType ?? '',
                      targetColor: setting.targetColor ?? '',
                      targetValue: setting.targetValue != null ? String( setting.targetValue ) : '',
                      createTicketOnTargetMiss: ( setting.sendAlertTime ? '1' : ( setting.alertThemeId ? '1' : '0' ) ),
                      ticketCreationHour: setting.sendAlertTime ? setting.sendAlertTime.slice( 0, 5 ) : '',
                      ticketTheme: setting.alertThemeId ?? '',
                      sendAlertTime: setting.sendAlertTime ? setting.sendAlertTime.slice( 0, 5 ) : '',
                      alertThemeId: setting.alertThemeId ?? '',
                      hasTarget: ( setting.targetValue != null || !!setting.targetName ) ? '1' : '0',
                      displayMin: setting.isUsedMin ? '1' : '0',
                      displayMax: setting.isUsedMax ? '1' : '0',
                      deltaMinMax: setting.deltaMinMax != null ? String( setting.deltaMinMax ) : '',
                      incrementValue: setting.incrementValue != null ? String( setting.incrementValue ) : '',
                      incrementFrequencyDays: setting.incrementFrequencyDays != null ? String( setting.incrementFrequencyDays ) : '',
                      readStart: normalizeReadStart( setting.readStart ),
                      readEnd: setting.readEnd,
                      additionalIndicator: setting.additionalIndicator,
                    } );
                    setFormChanged( false );
                    setShowSettingForm( true );
                  } )}
                  onMarkDeleteSetting={handleMarkDeleteChartSetting}
                  onRestorePendingDelete={handleRestorePendingDelete}
                  onRemovePending={handleRemovePending}
                  onRemovePendingUpdate={handleRemovePendingUpdate}
                  onMoveSetting={handleMoveChartSetting}
                  onMovePendingSetting={handleMovePendingSetting}
                />
              )}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <SubmitButton
                label={t( 'common:cancel' )}
                onClick={isEditingMode ? handleCancelEdit : handleCancelCreate}
                disable={false}
                width="w-auto"
                bgColor="bg-white"
                textColor="text-gray-600"
                hoverBorderColor="group-hover:border-gray-400"
                bgColorHover="group-hover:bg-gray-100"
                textColorHover="group-hover:text-gray-800"
                textSize="text-xs"
              />
              <SubmitButton
                label={t( 'common:save' )}
                onClick={isEditingMode ? handleConfirmEdit : handleConfirmCreate}
                disable={( isEditingMode ? editingName.trim() : newSectionName.trim() ) === ''}
                width="w-auto"
                bgColor={( isEditingMode ? editingName.trim() : newSectionName.trim() ) === '' ? 'bg-green-200' : 'bg-green-500'}
                textColor="text-white"
                hoverBorderColor={( isEditingMode ? editingName.trim() : newSectionName.trim() ) === '' ? '' : 'group-hover:border-green-600'}
                bgColorHover={( isEditingMode ? editingName.trim() : newSectionName.trim() ) === '' ? '' : 'group-hover:bg-green-600'}
                textColorHover={( isEditingMode ? editingName.trim() : newSectionName.trim() ) === '' ? '' : 'group-hover:text-white'}
                textSize="text-xs"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-1">
              <button
                type="button"
                onClick={handleAddSection}
                className="flex items-center justify-center w-7 h-7 rounded bg-green-200 text-green-800 hover:bg-green-300 border border-green-300"
              >
                <PlusIcon className="w-3 h-3 translate-y-[1px]" />
              </button>
              <button
                type="button"
                onClick={handleRemoveSection}
                disabled={!selectedSectionId}
                className={`flex items-center justify-center w-7 h-7 rounded border ${
                  !selectedSectionId
                    ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed'
                    : 'bg-red-200 text-red-800 border-red-300 hover:bg-red-300'
                }`}
              >
                <MinusIcon className="w-3 h-3 translate-y-[1px]" />
              </button>
              <button
                type="button"
                onClick={ () => {
                  if ( selectedSectionId ) {
                    const foundSection = sections.find( s => { return s.id === selectedSectionId; } );
                    if ( foundSection ) {
                      handleStartEdit( foundSection );
                    }
                  }
                } }
                disabled={
                  !selectedSectionId
                }
                className={`flex items-center justify-center w-7 h-7 rounded border ml-1 ${
                  !selectedSectionId
                    ? 'bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed'
                    : 'bg-blue-200 text-blue-800 border-blue-300 hover:bg-blue-300'
                }`}
              >
                <EditBoxIcon className="w-4 h-4" />
              </button>
            </div>

            {isCreating && (
              <div className="p-2 border border-blue-200 rounded-md bg-blue-50 max-w-md">
                <p className="text-xs font-medium text-gray-700 mb-1">
                  {t( 'translation:create_chart_section' )}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newSectionName}
                    onChange={e => { return setNewSectionName( e.target.value ); }}
                    maxLength={45}
                    className="flex-1 border border-blue-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
                    placeholder={t( 'translation:section_name_placeholder' )}
                  />
                  <SubmitButton
                    label={t( 'common:cancel' )}
                    onClick={handleCancelCreate}
                    disable={false}
                    width="w-auto"
                    bgColor="bg-white"
                    textColor="text-gray-600"
                    hoverBorderColor="group-hover:border-gray-400"
                    bgColorHover="group-hover:bg-gray-100"
                    textColorHover="group-hover:text-gray-800"
                    textSize="text-xs"
                  />
                  <SubmitButton
                    label={t( 'common:save' )}
                    onClick={handleConfirmCreate}
                    disable={newSectionName.trim() === ''}
                    width="w-auto"
                    bgColor={newSectionName.trim() === '' ? 'bg-green-100' : 'bg-green-500'}
                    textColor={newSectionName.trim() === '' ? 'text-green-300' : 'text-white'}
                    hoverBorderColor={newSectionName.trim() === '' ? '' : 'group-hover:border-green-600'}
                    bgColorHover={newSectionName.trim() === '' ? '' : 'group-hover:bg-green-600'}
                    textColorHover={newSectionName.trim() === '' ? '' : 'group-hover:text-white'}
                    textSize="text-xs font-medium"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {sections.map( ( section, index ) => {
                const canMoveLeft = index > 0;
                const canMoveRight = index < sections.length - 1;
                const isSelected = section.id === selectedSectionId;
                const isPendingSection = !isPersistedSection( section.id );

                return (
                  <div
                    key={section.id}
                    className={`flex items-center gap-1 rounded-md px-2 py-1 shadow-sm min-w-[150px] border ${
                      isPendingSection
                        ? ( isSelected ? 'bg-amber-50 border-amber-500 border-dashed' : 'bg-amber-50/60 border-amber-300 border-dashed' )
                        : ( isSelected ? 'bg-blue-50 border-blue-500' : 'bg-white border-blue-200' )
                    }`}
                  >
                    <button
                      type="button"
                      className={`text-blue-500 hover:text-blue-700 px-0.5 ${canMoveLeft ? '' : 'invisible'}`}
                      onClick={() => { return moveSection( index, index - 1 ); }}
                      disabled={!canMoveLeft}
                    >
                      <ChevronsLeftIcon className="w-3 h-3" />
                    </button>

                    <button
                      type="button"
                      onClick={() => { return setSelectedSectionId( section.id ); }}
                      className={`flex-1 px-2 py-0.5 text-xs text-center uppercase tracking-wide truncate bg-transparent ${
                        isPendingSection ? 'text-amber-800' : 'text-gray-700'
                      }`}
                    >
                      {section.name || t( 'translation:section_name_placeholder' )}
                    </button>

                    <button
                      type="button"
                      className={`text-blue-500 hover:text-blue-700 px-0.5 ${canMoveRight ? '' : 'invisible'}`}
                      onClick={() => { return moveSection( index, index + 1 ); }}
                      disabled={!canMoveRight}
                    >
                      <ChevronsRightIcon className="w-3 h-3" />
                    </button>
                  </div>
                );
              } )}
            </div>
          </>
        )}
      </div>
    </div>
  );
} );

ChartSectionList.displayName = 'ChartSectionList';
